import { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ClientDetail, ClientContract, ClientLead } from "@/hooks/useAMClientDetail";
import type { Json } from "@/integrations/supabase/types";

export interface HealthScoreResult {
  overall: number;
  status: "healthy" | "neutral" | "at_risk" | "critical";
  delivery: number;
  engagement: number;
  billing: number;
  contract: number;
  riskFactors: string[];
  trend7d: number | null;
  trend30d: number | null;
  history: { date: string; score: number }[];
  loading: boolean;
}

const GRACE_PERIOD_DAYS = 7;

function clamp(v: number, min = 0, max = 100) {
  return Math.max(min, Math.min(max, v));
}

/** Returns true if the client's earliest active contract started less than GRACE_PERIOD_DAYS ago */
export function isNewClient(contracts: ClientContract[]): boolean {
  const active = contracts.filter((c) => c.status === "active");
  if (active.length === 0) return false;
  const earliest = Math.min(...active.map((c) => c.cycle_start));
  const daysSinceStart = (Date.now() - earliest) / (1000 * 60 * 60 * 24);
  return daysSinceStart < GRACE_PERIOD_DAYS;
}

export function computeDelivery(contracts: ClientContract[]): number {
  const active = contracts.filter((c) => c.status === "active");
  if (active.length === 0) return 50;
  if (isNewClient(contracts)) return 50;
  const scores = active.map((c) =>
    c.total_leads_committed === 0 ? 100 : clamp((c.leads_delivered / c.total_leads_committed) * 100)
  );
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

export function computeEngagement(leads: ClientLead[], contracts: ClientContract[]): number {
  if (leads.length === 0) return 50;
  if (isNewClient(contracts) && leads.length < 5) return 50;
  const engaged = leads.filter((l) =>
    ["contacted", "qualified", "won"].includes(l.agent_status)
  ).length;
  return clamp(Math.round((engaged / leads.length) * 100));
}

export function computeBilling(contracts: ClientContract[]): number {
  let score = 100;
  const active = contracts.filter((c) => c.status === "active");
  if (active.length === 0) score -= 40;
  if (active.some((c) => c.paused_at !== null)) score -= 30;
  if (active.some((c) => c.cancel_at_period_end)) score -= 50;
  return clamp(score);
}

export function computeContract(contracts: ClientContract[]): number {
  const active = contracts.filter((c) => c.status === "active");
  if (active.length === 0) return 0;
  let score = 100;
  if (active.some((c) => c.paused_at !== null)) score -= 30;
  if (active.some((c) => c.cancel_at_period_end)) score -= 50;
  return clamp(score);
}

export function getStatus(overall: number): HealthScoreResult["status"] {
  if (overall >= 70) return "healthy";
  if (overall >= 50) return "neutral";
  if (overall >= 30) return "at_risk";
  return "critical";
}

export function getRiskFactors(
  delivery: number,
  engagement: number,
  contracts: ClientContract[]
): string[] {
  const factors: string[] = [];
  const active = contracts.filter((c) => c.status === "active");
  const gracePeriod = isNewClient(contracts);
  if (!gracePeriod && engagement < 40) factors.push(`Low contact rate (${engagement}%)`);
  if (active.some((c) => c.paused_at !== null)) factors.push("Contract paused");
  if (active.some((c) => c.cancel_at_period_end)) factors.push("Contract cancelling");
  if (active.length === 0) factors.push("No active contracts");
  if (!gracePeriod && delivery < 60) factors.push(`Behind on delivery (${delivery}%)`);
  if (gracePeriod) factors.push("New client (grace period)");
  return factors;
}

/** Compute the overall weighted score from component scores */
export function computeOverall(delivery: number, engagement: number, billing: number, contractScore: number): number {
  return clamp(Math.round(delivery * 0.3 + engagement * 0.25 + billing * 0.25 + contractScore * 0.2));
}

export function useHealthScore(
  agent: ClientDetail | null,
  contracts: ClientContract[],
  leads: ClientLead[],
  agentId: string | undefined
): HealthScoreResult {
  const [history, setHistory] = useState<{ date: string; score: number }[]>([]);
  const [trend7d, setTrend7d] = useState<number | null>(null);
  const [trend30d, setTrend30d] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const delivery = useMemo(() => computeDelivery(contracts), [contracts]);
  const engagement = useMemo(() => computeEngagement(leads, contracts), [leads, contracts]);
  const billing = useMemo(() => computeBilling(contracts), [contracts]);
  const contract = useMemo(() => computeContract(contracts), [contracts]);

  const overall = computeOverall(delivery, engagement, billing, contract);
  const status = getStatus(overall);
  const riskFactors = useMemo(
    () => getRiskFactors(delivery, engagement, contracts),
    [delivery, engagement, contracts]
  );

  // Track previous insert to avoid re-inserting on every render
  const lastInsertRef = useRef<string>("");

  useEffect(() => {
    if (!agentId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();

        const { data: scores } = await supabase
          .from("client_health_scores")
          .select("overall_score, computed_at, score_delta_7d, score_delta_30d")
          .eq("agent_id", agentId)
          .gte("computed_at", ninetyDaysAgo)
          .order("computed_at", { ascending: false });

        if (cancelled) return;

        const rows = scores || [];
        const hist = rows.map((r) => ({
          date: r.computed_at ?? new Date().toISOString(),
          score: r.overall_score,
        }));
        setHistory(hist);

        // Insert a new snapshot if: no scores exist, latest is > 6h old, or score changed significantly
        const latestStored = rows.length > 0 ? rows[0] : null;
        const latestTime = latestStored?.computed_at
          ? new Date(latestStored.computed_at).getTime()
          : 0;
        const isStale = !latestStored || Date.now() - latestTime > 6 * 60 * 60 * 1000;
        const scoreDrifted = latestStored && Math.abs(latestStored.overall_score - overall) >= 3;
        const needsInsert = isStale || scoreDrifted;

        // Dedup key to prevent re-inserting the same score on rapid re-renders
        const insertKey = `${agentId}:${overall}:${status}:${delivery}:${engagement}`;

        if (needsInsert && agent && insertKey !== lastInsertRef.current) {
          lastInsertRef.current = insertKey;

          // Compute deltas by finding the closest score to 7d and 30d ago
          const now = Date.now();
          const score7d = rows.find((r) => {
            if (!r.computed_at) return false;
            const t = new Date(r.computed_at).getTime();
            return Math.abs(t - (now - 7 * 86400000)) < 2 * 86400000;
          });
          const score30d = rows.find((r) => {
            if (!r.computed_at) return false;
            const t = new Date(r.computed_at).getTime();
            return Math.abs(t - (now - 30 * 86400000)) < 3 * 86400000;
          });
          const d7 = score7d ? overall - score7d.overall_score : null;
          const d30 = score30d ? overall - score30d.overall_score : null;

          setTrend7d(d7);
          setTrend30d(d30);

          await supabase.from("client_health_scores").insert({
            agent_id: agentId,
            overall_score: overall,
            health_status: status,
            delivery_score: delivery,
            engagement_score: engagement,
            billing_score: billing,
            contract_score: contract,
            score_delta_7d: d7,
            score_delta_30d: d30,
            risk_factors: riskFactors as unknown as Json,
          });

          if (!cancelled) {
            setHistory((prev) => [
              { date: new Date().toISOString(), score: overall },
              ...prev,
            ]);
          }
        } else if (rows.length > 0) {
          setTrend7d(rows[0].score_delta_7d ?? null);
          setTrend30d(rows[0].score_delta_30d ?? null);
        }
      } catch (e) {
        console.error("useHealthScore error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [agentId, agent?.id, overall, status, delivery, engagement, billing, contract, riskFactors]);

  return {
    overall,
    status,
    delivery,
    engagement,
    billing,
    contract,
    riskFactors,
    trend7d,
    trend30d,
    history,
    loading,
  };
}
